import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UITextElement } from "../../common_ui_elements/ui_text_element.js";

interface CartShakeOptions
{
  bounce: number;
  sway: number;
  bump: number;
  lag: number;
  rot_deg?: number;
  rot_freq?: number;
  rot_phase?: number;
  base_rot?: number;
}

interface CartShakePart
{
  el: UIElement;
  base: Vector2;
  anchor: Vector2;
  bounce_amp: number;
  sway_amp: number;
  bump_amp: number;
  lag: number;
  rot_amp: number;
  rot_freq: number;
  rot_phase: number;
  base_rot: number;
}

export class UICart extends UIElement
{
  private static readonly SHAKE_RISE_SPEED = 7;
  private static readonly SHAKE_FALL_SPEED = 6.5;
  private static readonly SHAKE_EPSILON = 0.002;

  private static readonly HEAD_TILT_DRIVE = -7 * Math.PI / 180;
  private static readonly HEAD_TILT_LIMIT = -15 * Math.PI / 180;
  private static readonly HEAD_TILT_FORWARD_MAX = 13 * Math.PI / 180;
  private static readonly HEAD_START_IMPULSE = -2.2;
  private static readonly HEAD_STOP_IMPULSE = 3.2;
  private static readonly HEAD_STIFFNESS = 26;
  private static readonly HEAD_DAMPING = 6.5;
  private static readonly HEAD_WOBBLE_DEG = 1.4;

  private static readonly BLINK_HOLD_MIN = 0.11;
  private static readonly BLINK_DOUBLE_CHANCE = 0.3;
  private static readonly BLINK_DOUBLE_GAP = 0.12;

  private static readonly HEAD_TILT = 19 * Math.PI / 180;

  // ---- Cannon ----
  // Base resting angle of the cannon barrel.
  private static readonly CANNON_TILT = -35 * Math.PI / 180;
  // Angular kick applied when the cart stops. Positive = nose dips forward,
  // then the spring pulls it sharply back to CANNON_TILT and settles.
  private static readonly CANNON_STOP_IMPULSE = 5.5;
  // Spring pulling the cannon back to its base angle. Higher = snappier return.
  private static readonly CANNON_STIFFNESS = 34;
  // Damping bleeds the wobble off. Lower = more bounce before it settles.
  private static readonly CANNON_DAMPING = 7.5;
  // Clamp so the tilt never swings past these offsets from the base angle.
  private static readonly CANNON_TILT_BACK_MAX = -10 * Math.PI / 180;
  private static readonly CANNON_TILT_FORWARD_MAX = 16 * Math.PI / 180;

  // ---- Wheel ----
  // Peak spin speed of the wheel circle, in radians per second, at full drive.
  // Negative spins the wheel the way the cart travels (left). Flip the sign to
  // reverse. Actual speed scales with shake_env, so it eases in and out with the
  // ride instead of snapping on/off.
  private static readonly WHEEL_SPIN_SPEED = 8;

  private car_img_id: ImgID;

  private shake_parts: CartShakePart[] = [];
  private shake_env = 0;
  private shake_target = 0;
  private last_now = 0;
  private readonly seed = Math.random() * 100;

  private head_part!: CartShakePart;
  private eyes_part!: CartShakePart;

  private tiger_head!: UIImage;
  private tiger_eyes!: UIImage;

  private car_cannon!: UIImage;
  private wheel_circle!: UIImage;
  private car_wheel_circle!: UIImage;

  private driving = false;

  private blink_timer = 1 + Math.random() * 2;
  private blink_hold = 0;
  private blink_queued = 0;

  private head_tilt = 0;
  private head_tilt_vel = 0;

  // Cannon spring state: offset from the base angle, plus its velocity.
  private cannon_tilt = 0;
  private cannon_tilt_vel = 0;

  // Accumulated wheel rotation, in radians.
  private wheel_angle = 0;

  private readonly tmp_pos = new Vector2( 0, 0 );

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    level: number,
    car_img_id: ImgID
  )
  {
    super( ctx, x, y, width, height, false, false );

    this.car_img_id = car_img_id;

    this.enableRotation();
    this.createCart( level );
  }

  private createCart( level: number ): void
  {
    const roof_base_far = this.image( 105, 82, 68, 297, ImgID.UI_CarRoofBase );

    const balls = this.image( 37, 275, 284.65, 272.63, ImgID.UI_CarBalls );

    const car = this.image( 0, 0, 719, 801, this.car_img_id );
    const car_wheel_circle = this.image( 484, 637, 90, 90, ImgID.UI_CarWheelCircle );

    const tiger_body = this.image( 199.5, 379.6, 315.15, 196.91, ImgID.UI_TigerBody );
    const tiger_head = this.image( 215, 146, 309, 311, ImgID.UI_TigerHead );
    const tiger_eyes = this.image( 288.8, 294.9, 147.5, 52.6, ImgID.UI_TigerEyes );

    const car_cannon = this.image( 38 - 390, 442, 390, 219, ImgID.UI_CarCannon );
    const car_cannon_wheel = this.image( -75 - 174, 575, 174, 175, ImgID.UI_CarCannonWheel );
    const car_cannon_wheel_circle = this.image( -95 - 141, 597, 141, 141, ImgID.UI_CarCannonWheelCircle );
    const car_cannon_wheel_base = this.image( 60 - 390, 540, 423, 231, ImgID.UI_CarCannonWheelBase );

    const level_caption = this.createLevelText(
      135, 640, 125, 41, 30, { localization_key: "LEVEL", text_color: "#F3DE9A" }
    );

    const level_number = this.createLevelText(
      121, 500, 150, 170, 120, { text: level.toString(), text_color: ColorScheme.white_70, shadow_blur: LayoutScaler.widthFromDesign( 10 ), shadow_offset_y: LayoutScaler.heightFromDesign( 6 ), shadow_color: "#540E00", outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ), outline_color: ColorScheme.white_70, }
    );

    const level_number_top = this.createLevelText(
      121, 500, 150, 170, 120, { text: level.toString(), text_color: ColorScheme.white, shadow_blur: LayoutScaler.widthFromDesign( 4 ), shadow_offset_y: LayoutScaler.heightFromDesign( 4 ), shadow_color: "#540E00", }
    );

    this.addChild( roof_base_far );

    this.addChild( car_cannon_wheel_base );
    this.addChild( car_cannon );
    this.addChild( car_cannon_wheel );
    this.addChild( car_cannon_wheel_circle );

    this.addChild( tiger_body );
    this.addChild( tiger_head );
    this.addChild( tiger_eyes );

    this.addChild( balls );
    this.addChild( car );
    this.addChild( car_wheel_circle );

    this.addChild( level_caption );
    this.addChild( level_number );
    this.addChild( level_number_top );

    this.tiger_head = tiger_head;
    this.tiger_eyes = tiger_eyes;
    this.car_cannon = car_cannon;
    this.wheel_circle = car_cannon_wheel_circle;
    this.car_wheel_circle = car_wheel_circle;

    tiger_eyes.setOpacityImmediate( 0 );

    tiger_head.enableRotation();
    tiger_eyes.enableRotation();
    car_cannon_wheel_circle.enableRotation();
    car_cannon.enableRotation();
    car_wheel_circle.enableRotation();

    // The cannon rests at its base angle from the start.
    car_cannon.setRotationImmediate( UICart.CANNON_TILT );

    this.registerShakePart( balls, { bounce: 4.6, sway: 1.5, bump: 2.6, lag: 0.14 } );

    const head_shake: CartShakeOptions = { bounce: 4.2, sway: 1.5, bump: 2.1, lag: 0.10 };

    this.head_part = this.registerShakePart( tiger_head, {
      ...head_shake, base_rot: UICart.HEAD_TILT
    } );

    this.eyes_part = this.registerShakePart( tiger_eyes, {
      ...head_shake, base_rot: UICart.HEAD_TILT
    } );

    this.applyBaseRotations();
  }

  private applyBaseRotations(): void
  {
    for ( const p of this.shake_parts )
    {
      if ( p.base_rot !== 0 )
        p.el.setRotationImmediate( p.base_rot );
    }
  }

  private registerShakePart( el: UIElement, opts: CartShakeOptions ): CartShakePart
  {
    const pos = el.pixelPosImportant();
    const rot_amp = ( opts.rot_deg ?? 0 ) * Math.PI / 180;

    const part: CartShakePart = {
      el,
      base: pos.clone(),
      anchor: pos.clone(),
      bounce_amp: opts.bounce,
      sway_amp: opts.sway,
      bump_amp: opts.bump,
      lag: opts.lag,
      rot_amp,
      rot_freq: opts.rot_freq ?? 0,
      rot_phase: opts.rot_phase ?? 0,
      base_rot: opts.base_rot ?? 0,
    };

    if ( rot_amp !== 0 || part.base_rot !== 0 )
      el.enableRotation();

    this.shake_parts.push( part );

    return part;
  }

  private image(
    design_x: number, design_y: number,
    design_w: number, design_h: number,
    img_id: ImgID
  ): UIImage
  {
    return new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      LayoutScaler.widthFromDesign( design_w ),
      LayoutScaler.heightFromDesign( design_h ),
      img_id,
      { direction_x: 1, direction_y: 1 }
    );
  }

  private createLevelText(
    design_x: number, design_y: number,
    design_w: number, design_h: number,
    font_size_design: number,
    text_opts: { text?: string; localization_key?: string, text_color?: string, shadow_color?: string, shadow_offset_y?: number, shadow_blur?: number, outline_width?: number, outline_color?: string, }
  ): UITextElement
  {
    return new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      LayoutScaler.widthFromDesign( design_w ),
      LayoutScaler.heightFromDesign( design_h ),
      {
        ...text_opts,
        font_size: LayoutScaler.textSizeFromDesign( font_size_design ),
        font_weight: 800,
        text_style: TextStyleScheme.geologica,
        auto_wrap: false,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 28 ),
      }
    );
  }

  public moveWheels(): void
  {
    this.shake_target = 1;
    this.driving = true;

    this.head_tilt_vel = UICart.HEAD_START_IMPULSE;
  }

  public stopWheels(): void
  {
    this.shake_target = 0;
    this.driving = false;
  }

  public tiltBeforeStop(): void
  {
    this.head_tilt_vel = UICart.HEAD_STOP_IMPULSE;

    // Kick the cannon forward; the spring in updateCannon snaps it back.
    this.cannon_tilt_vel = UICart.CANNON_STOP_IMPULSE;
  }

  private dom_ride_anims: Animation[] = [];
  private dom_ride_baked = false;

  public bakeDomRide( now: number, tilt_at_ms: number, stop_at_ms: number ): void
  {
    if ( !this.dom_mode )
      return;

    this.cancelDomRide();
    this.dom_ride_baked = true;

    const STEP = 1 / 60;
    const MAX_MS = stop_at_ms + 4000;
    const parts = this.shake_parts;
    const tracks = new Map<UIElement, { pos: Vector2, rot: number }[]>();
    const track_of = ( el: UIElement ): { pos: Vector2, rot: number }[] =>
    {
      let t = tracks.get( el );
      if ( !t ) { t = []; tracks.set( el, t ); }
      return t;
    };

    const sim = {
      env: this.shake_env,
      target: 1,
      driving: true,
      head_tilt: this.head_tilt,
      head_vel: UICart.HEAD_START_IMPULSE,
      cannon_tilt: this.cannon_tilt,
      cannon_vel: this.cannon_tilt_vel,
      wheel: this.wheel_angle,
    };

    let tilted = false;
    let stopped = false;
    let ms = 0;
    let settled_ms = -1;
    const two_pi = Math.PI * 2;
    const scale = LayoutScaler.getScale();

    while ( ms <= MAX_MS )
    {
      if ( !tilted && ms >= tilt_at_ms )
      {
        tilted = true;
        sim.head_vel = UICart.HEAD_STOP_IMPULSE;
        sim.cannon_vel = UICart.CANNON_STOP_IMPULSE;
      }
      if ( !stopped && ms >= stop_at_ms )
      {
        stopped = true;
        sim.target = 0;
        sim.driving = false;
      }

      const dt = STEP;
      const speed = sim.target > sim.env ? UICart.SHAKE_RISE_SPEED : UICart.SHAKE_FALL_SPEED;
      sim.env += ( sim.target - sim.env ) * Math.min( 1, dt * speed );
      if ( sim.target === 0 && sim.env < UICart.SHAKE_EPSILON )
        sim.env = 0;

      const t = ( now + ms ) / 1000 + this.seed;
      const env = sim.env;
      for ( const p of parts )
      {
        const tp = t - p.lag;
        const bounce =
          0.55 * Math.sin( two_pi * 2.2 * tp ) +
          0.30 * Math.sin( two_pi * 3.7 * tp + 1.3 ) +
          0.15 * Math.sin( two_pi * 5.9 * tp + 2.6 );
        const bump = Math.pow( Math.max( 0, Math.sin( two_pi * 0.7 * tp + 0.9 ) ), 6 );
        const dx = env * scale * p.sway_amp * Math.sin( two_pi * 0.85 * tp + 0.4 );
        const dy = env * scale * ( p.bounce_amp * bounce - p.bump_amp * bump );
        const rot = p.rot_amp !== 0
          ? p.base_rot + env * p.rot_amp * Math.sin( two_pi * p.rot_freq * tp + p.rot_phase )
          : p.base_rot;
        track_of( p.el ).push( { pos: new Vector2( p.base.x + dx, p.base.y + dy ), rot } );
      }

      const head_target = sim.driving ? UICart.HEAD_TILT_DRIVE : 0;
      sim.head_vel += ( head_target - sim.head_tilt ) * UICart.HEAD_STIFFNESS * dt;
      sim.head_vel -= sim.head_vel * UICart.HEAD_DAMPING * dt;
      sim.head_tilt += sim.head_vel * dt;
      sim.head_tilt = Math.min( Math.max( sim.head_tilt, UICart.HEAD_TILT_LIMIT ), UICart.HEAD_TILT_FORWARD_MAX );
      const wobble = env * UICart.HEAD_WOBBLE_DEG * Math.PI / 180 * Math.sin( ( now + ms ) / 1000 * Math.PI * 2 * 1.9 + this.seed );
      const head_angle = sim.head_tilt + wobble;
      const head_track = track_of( this.tiger_head );
      const head_last = head_track[head_track.length - 1];
      head_last.rot = UICart.HEAD_TILT + head_angle;
      const eyes_track = track_of( this.tiger_eyes );
      const eyes_last = eyes_track[eyes_track.length - 1];
      eyes_last.pos = this.rotatedAroundHead( head_last.pos, eyes_last.pos, this.tiger_eyes, head_angle );
      eyes_last.rot = this.eyes_part.base_rot + head_angle;

      sim.cannon_vel += ( 0 - sim.cannon_tilt ) * UICart.CANNON_STIFFNESS * dt;
      sim.cannon_vel -= sim.cannon_vel * UICart.CANNON_DAMPING * dt;
      sim.cannon_tilt += sim.cannon_vel * dt;
      sim.cannon_tilt = Math.min( Math.max( sim.cannon_tilt, UICart.CANNON_TILT_BACK_MAX ), UICart.CANNON_TILT_FORWARD_MAX );
      track_of( this.car_cannon ).push( { pos: this.car_cannon.pixelPosImportant().clone(), rot: UICart.CANNON_TILT + sim.cannon_tilt } );

      sim.wheel += UICart.WHEEL_SPIN_SPEED * env * dt;
      track_of( this.wheel_circle ).push( { pos: this.wheel_circle.pixelPosImportant().clone(), rot: sim.wheel } );
      track_of( this.car_wheel_circle ).push( { pos: this.car_wheel_circle.pixelPosImportant().clone(), rot: sim.wheel } );

      const settled = stopped && sim.env === 0
        && Math.abs( sim.head_tilt ) <= 0.0005 && Math.abs( sim.head_vel ) <= 0.0005
        && Math.abs( sim.cannon_tilt ) <= 0.0005 && Math.abs( sim.cannon_vel ) <= 0.0005;
      if ( settled )
      {
        settled_ms = ms;
        break;
      }
      ms += STEP * 1000;
    }

    const total_ms = settled_ms >= 0 ? settled_ms : MAX_MS;
    for ( const [ el, samples ] of tracks )
    {
      if ( samples.length < 2 )
        continue;
      const stride = Math.max( 1, Math.floor( samples.length / 240 ) );
      const keyframes: Keyframe[] = [];
      for ( let i = 0; i < samples.length; i += stride )
      {
        const s = samples[i];
        keyframes.push( { translate: `${s.pos.x}px ${s.pos.y}px`, rotate: `${s.rot}rad`, offset: Math.min( 1, i / ( samples.length - 1 ) ), easing: "linear" } );
      }
      const last = samples[samples.length - 1];
      keyframes.push( { translate: `${last.pos.x}px ${last.pos.y}px`, rotate: `${last.rot}rad`, offset: 1 } );
      try
      {
        const anim = el.getDomNode().animate( keyframes, { duration: total_ms, fill: "forwards" } );
        anim.finished.then( () => anim.cancel() ).catch( () => {} );
        this.dom_ride_anims.push( anim );
      }
      catch { }
    }

    this.wheel_angle = sim.wheel % ( Math.PI * 2 );
  }

  private rotatedAroundHead( head_pos: Vector2, part_pos: Vector2, el: UIImage, angle: number ): Vector2
  {
    const head_rect = this.tiger_head.getRect();
    const el_rect = el.getRect();
    const pivot_x = head_pos.x + head_rect.getW() / 2;
    const pivot_y = head_pos.y + head_rect.getH() / 2;
    const half_w = el_rect.getW() / 2;
    const half_h = el_rect.getH() / 2;
    const dx = part_pos.x + half_w - pivot_x;
    const dy = part_pos.y + half_h - pivot_y;
    const cos = Math.cos( angle );
    const sin = Math.sin( angle );
    return new Vector2(
      pivot_x + dx * cos - dy * sin - half_w,
      pivot_y + dx * sin + dy * cos - half_h
    );
  }

  private cancelDomRide(): void
  {
    for ( const anim of this.dom_ride_anims )
      anim.cancel();
    this.dom_ride_anims = [];
    this.dom_ride_baked = false;
  }

  public override cleanup(): void
  {
    this.cancelDomRide();
    super.cleanup();
  }

  protected updateBasic( now: number ): void
  {
    const dt = this.last_now > 0
      ? Math.min( 0.05, ( now - this.last_now ) / 1000 )
      : 0;

    this.last_now = now;

    if ( dt > 0 && this.dom_ride_baked )
    {
      if ( this.updateBlink( dt ) )
        this.forceRedraw();
      if ( !this.driving && this.shake_target === 0 )
      {
        this.shake_env = 0;
        this.head_tilt = 0;
        this.head_tilt_vel = 0;
        this.cannon_tilt = 0;
        this.cannon_tilt_vel = 0;
      }
    }
    else if ( dt > 0 )
    {
      this.updateShake( now, dt );

      let redraw = false;

      redraw = this.updateBlink( dt ) || redraw;
      redraw = this.updateHead( now, dt ) || redraw;
      redraw = this.updateCannon( dt ) || redraw;
      redraw = this.updateWheel( dt ) || redraw;

      if ( redraw )
        this.forceRedraw();
    }

    super.updateBasic( now );
  }

  private updateWheel( dt: number ): boolean
  {
    // Spin speed follows the ride envelope, so the wheel accelerates as the cart
    // starts moving and coasts to a stop as it settles — no abrupt on/off.
    const speed = UICart.WHEEL_SPIN_SPEED * this.shake_env;

    // Idle: not moving and effectively stopped -> nothing to draw.
    if ( Math.abs( speed ) < 0.0005 )
      return false;

    this.wheel_angle += speed * dt;

    // Keep the accumulated angle bounded to avoid float drift over long sessions.
    const two_pi = Math.PI * 2;
    if ( this.wheel_angle > two_pi ) this.wheel_angle -= two_pi;
    else if ( this.wheel_angle < -two_pi ) this.wheel_angle += two_pi;

    this.wheel_circle.setRotationImmediate( this.wheel_angle );
    this.car_wheel_circle.setRotationImmediate( this.wheel_angle );

    return true;
  }

  private updateCannon( dt: number ): boolean
  {
    // Spring toward 0 offset (i.e. back to the base CANNON_TILT angle).
    this.cannon_tilt_vel += ( 0 - this.cannon_tilt ) * UICart.CANNON_STIFFNESS * dt;
    this.cannon_tilt_vel -= this.cannon_tilt_vel * UICart.CANNON_DAMPING * dt;
    this.cannon_tilt += this.cannon_tilt_vel * dt;

    this.cannon_tilt = Math.min(
      Math.max( this.cannon_tilt, UICart.CANNON_TILT_BACK_MAX ),
      UICart.CANNON_TILT_FORWARD_MAX
    );

    // Settled: pin to base and stop redrawing.
    if ( Math.abs( this.cannon_tilt ) <= 0.0005 && Math.abs( this.cannon_tilt_vel ) <= 0.0005 )
    {
      if ( this.cannon_tilt === 0 )
        return false;

      this.cannon_tilt = 0;
      this.cannon_tilt_vel = 0;
      this.car_cannon.setRotationImmediate( UICart.CANNON_TILT );

      return true;
    }

    this.car_cannon.setRotationImmediate( UICart.CANNON_TILT + this.cannon_tilt );

    return true;
  }

  private updateShake( now: number, dt: number ): void
  {
    if ( this.shake_env === 0 && this.shake_target === 0 )
      return;

    const speed = this.shake_target > this.shake_env
      ? UICart.SHAKE_RISE_SPEED
      : UICart.SHAKE_FALL_SPEED;

    this.shake_env += ( this.shake_target - this.shake_env ) * Math.min( 1, dt * speed );

    if ( this.shake_target === 0 && this.shake_env < UICart.SHAKE_EPSILON )
    {
      this.shake_env = 0;
      this.resetShakeParts();
      this.forceRedraw();
      return;
    }

    const t = now / 1000 + this.seed;
    const scale = LayoutScaler.getScale();
    const env = this.shake_env;
    const two_pi = Math.PI * 2;

    for ( const p of this.shake_parts )
    {
      const tp = t - p.lag;

      const bounce =
        0.55 * Math.sin( two_pi * 2.2 * tp ) +
        0.30 * Math.sin( two_pi * 3.7 * tp + 1.3 ) +
        0.15 * Math.sin( two_pi * 5.9 * tp + 2.6 );

      const bump = Math.pow( Math.max( 0, Math.sin( two_pi * 0.7 * tp + 0.9 ) ), 6 );

      const dx = env * scale * p.sway_amp * Math.sin( two_pi * 0.85 * tp + 0.4 );
      const dy = env * scale * ( p.bounce_amp * bounce - p.bump_amp * bump );

      p.anchor.set( p.base.x + dx, p.base.y + dy );
      p.el.setPixelPos( p.anchor, true );

      if ( p.rot_amp !== 0 )
      {
        p.el.setRotationImmediate(
          p.base_rot + env * p.rot_amp * Math.sin( two_pi * p.rot_freq * tp + p.rot_phase )
        );
      }
    }

    this.forceRedraw();
  }

  private resetShakeParts(): void
  {
    for ( const p of this.shake_parts )
    {
      p.anchor.copyFrom( p.base );
      p.el.setPixelPos( p.base, true );

      p.el.setRotationImmediate( p.base_rot );
    }

    this.setRotationImmediate( 0 );
  }

  private updateBlink( dt: number ): boolean
  {
    if ( this.blink_hold > 0 )
    {
      this.blink_hold -= dt;

      if ( this.blink_hold > 0 )
        return false;

      this.tiger_eyes.setOpacityImmediate( 0 );

      if ( this.blink_queued > 0 )
      {
        this.blink_queued--;
        this.blink_timer = UICart.BLINK_DOUBLE_GAP;
      }
      else
      {
        this.blink_timer = this.driving
          ? 1.2 + Math.random() * 1.8
          : 2.2 + Math.random() * 3.5;
      }

      return true;
    }

    this.blink_timer -= dt;

    if ( this.blink_timer > 0 )
      return false;

    this.tiger_eyes.setOpacityImmediate( 1 );
    this.blink_hold = UICart.BLINK_HOLD_MIN + Math.random() * 0.05;

    if ( this.blink_queued === 0 && Math.random() < UICart.BLINK_DOUBLE_CHANCE )
      this.blink_queued = 1;

    return true;
  }

  private updateHead( now: number, dt: number ): boolean
  {
    const target = this.driving ? UICart.HEAD_TILT_DRIVE : 0;

    this.head_tilt_vel += ( target - this.head_tilt ) * UICart.HEAD_STIFFNESS * dt;
    this.head_tilt_vel -= this.head_tilt_vel * UICart.HEAD_DAMPING * dt;
    this.head_tilt += this.head_tilt_vel * dt;

    this.head_tilt = Math.min(
      Math.max( this.head_tilt, UICart.HEAD_TILT_LIMIT ),
      UICart.HEAD_TILT_FORWARD_MAX
    );

    if ( Math.abs( this.head_tilt ) <= 0.0005 && Math.abs( this.head_tilt_vel ) <= 0.0005 )
      return false;

    const wobble = this.shake_env * UICart.HEAD_WOBBLE_DEG * Math.PI / 180
      * Math.sin( now / 1000 * Math.PI * 2 * 1.9 + this.seed );

    const angle = this.head_tilt + wobble;

    this.tiger_head.setRotationImmediate( UICart.HEAD_TILT + angle );

    this.rotateAroundHead( this.tiger_eyes, this.eyes_part, angle );

    return true;
  }

  private rotateAroundHead(
    el: UIImage,
    part: CartShakePart,
    angle: number
  ): void
  {
    const head_rect = this.tiger_head.getRect();
    const el_rect = el.getRect();

    const pivot_x = this.head_part.anchor.x + head_rect.getW() / 2;
    const pivot_y = this.head_part.anchor.y + head_rect.getH() / 2;

    const half_w = el_rect.getW() / 2;
    const half_h = el_rect.getH() / 2;

    const dx = part.anchor.x + half_w - pivot_x;
    const dy = part.anchor.y + half_h - pivot_y;

    const cos = Math.cos( angle );
    const sin = Math.sin( angle );

    this.tmp_pos.set(
      pivot_x + dx * cos - dy * sin - half_w,
      pivot_y + dx * sin + dy * cos - half_h
    );

    el.setPixelPos( this.tmp_pos, true );
    el.setRotationImmediate( part.base_rot + angle );
  }

  public name(): string
  {
    return "UICart";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}